import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';
import RogersFamilyMealPlanner from './RogersFamilyMealPlanner';

// ── Error Boundary: last-resort guard so a render exception cannot leave
//    the user looking at a blank page. Shows the error + a Reset button.
class AppErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error('App crashed:', err, info); }
  handleReset = () => {
    try { sessionStorage.clear(); } catch {}
    window.location.reload();
  };
  render() {
    if (this.state.err) {
      return (
        <div style={{display:"flex",justifyContent:"center",alignItems:"center",minHeight:"100vh",background:"#faf9f6",fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",padding:"24px"}}>
          <div style={{background:"white",padding:"32px",borderRadius:"12px",boxShadow:"0 4px 20px rgba(0,0,0,0.08)",maxWidth:"480px",width:"100%"}}>
            <div style={{fontSize:"32px",marginBottom:"12px"}}>🍽️</div>
            <h2 style={{color:"#2d6a4f",marginTop:0}}>Something went wrong</h2>
            <p style={{color:"#555",fontSize:"14px",lineHeight:"1.5"}}>
              The app hit an error and couldn't recover on its own. Click Reset below to reload.
              If it keeps happening, tell Claude what you were doing.
            </p>
            <pre style={{background:"#f3f4f6",padding:"10px",borderRadius:"6px",fontSize:"11px",color:"#666",overflowX:"auto",whiteSpace:"pre-wrap"}}>
              {String(this.state.err?.message || this.state.err)}
            </pre>
            <button onClick={this.handleReset}
              style={{padding:"10px 20px",background:"#2d6a4f",color:"white",border:"none",borderRadius:"8px",fontWeight:"600",fontSize:"14px",cursor:"pointer",marginTop:"12px"}}>
              Reset &amp; Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ═══════════════════════════════════════════════
//  APP WRAPPER — Auth (Google Sign-In) + Multi-Account Calendar
// ═══════════════════════════════════════════════

const GOOGLE_CLIENT_ID = '914866581244-pte9q0n3ofonjio6vd28jvluj2abu5sa.apps.googleusercontent.com';
const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/userinfo.email';

// ── Allowed emails for APP sign-in (lowercase) ──────────────────────────
const ALLOWED_EMAILS = [
  'rogersdna15@gmail.com',
  'david@providenceswfl.com',
];

function isEmailAllowed(email) {
  return ALLOWED_EMAILS.includes((email || '').toLowerCase().trim());
}

// ── Helpers to persist linked accounts in sessionStorage ──────────────
function loadLinkedAccounts() {
  try {
    const raw = sessionStorage.getItem('gcal_accounts');
    if (!raw) return [];
    const accounts = JSON.parse(raw);
    // Filter out expired tokens
    return accounts.filter(a => a.expiresAt > Date.now());
  } catch { return []; }
}

function saveLinkedAccounts(accounts) {
  sessionStorage.setItem('gcal_accounts', JSON.stringify(accounts));
}

export default function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  // Multi-account calendar state: [{ email, token, expiresAt, calendars: [{id, name}] }]
  const [linkedAccounts, setLinkedAccounts] = useState(() => loadLinkedAccounts());

  const googleBtnRef = useRef(null);
  const gsiInitialized = useRef(false);

  // Persist linked accounts whenever they change
  useEffect(() => {
    saveLinkedAccounts(linkedAccounts);
  }, [linkedAccounts]);

  // Handle the Google credential response (app sign-in)
  const handleCredentialResponse = useCallback(async (response) => {
    try {
      setAuthError('');
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: 'google',
        token: response.credential,
      });
      if (error) {
        console.error('Supabase auth error:', error);
        setAuthError('Sign-in failed: ' + error.message);
        return;
      }
      const userEmail = data?.user?.email;
      if (!isEmailAllowed(userEmail)) {
        console.warn('Unauthorized email attempted login:', userEmail);
        await supabase.auth.signOut();
        setAuthError('Access denied. This app is restricted to authorized accounts only.');
        return;
      }
    } catch (err) {
      console.error('Sign-in error:', err);
      setAuthError('Sign-in failed. Please try again.');
    }
  }, []);

  // Link a Google account for calendar access (any Google account)
  const linkCalendarAccount = useCallback(() => {
    if (!window.google?.accounts?.oauth2) return;

    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: CALENDAR_SCOPE,
      prompt: '', // Shows account chooser if multiple accounts signed in
      callback: async (tokenResponse) => {
        if (!tokenResponse?.access_token) return;

        const token = tokenResponse.access_token;
        const expiresAt = Date.now() + (tokenResponse.expires_in * 1000);

        // Get the email for this token
        let email = 'Unknown';
        try {
          const infoRes = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (infoRes.ok) {
            const info = await infoRes.json();
            email = info.email || 'Unknown';
          }
        } catch {}

        // Discover this account's calendars
        let calendars = [];
        try {
          const calRes = await fetch('https://www.googleapis.com/calendar/v3/users/me/calendarList?minAccessRole=reader', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (calRes.ok) {
            const calData = await calRes.json();
            calendars = (calData.items || [])
              .filter(c => !c.id.includes('#holiday@group') && !c.id.includes('#contacts@group') && !c.id.includes('addressbook'))
              .map(c => ({ id: c.id, name: c.summaryOverride || c.summary || c.id }));
          }
        } catch {}

        // If no calendars found, at least include primary
        if (calendars.length === 0) {
          calendars = [{ id: 'primary', name: email }];
        }

        setLinkedAccounts(prev => {
          // Replace if this email is already linked, otherwise add
          const filtered = prev.filter(a => a.email.toLowerCase() !== email.toLowerCase());
          return [...filtered, { email, token, expiresAt, calendars }];
        });
      },
    });
    client.requestAccessToken();
  }, []);

  // Unlink a specific account
  const unlinkCalendarAccount = useCallback((email) => {
    setLinkedAccounts(prev => {
      const account = prev.find(a => a.email === email);
      // Revoke token
      if (account && window.google?.accounts?.oauth2) {
        try { window.google.accounts.oauth2.revoke(account.token); } catch {}
      }
      return prev.filter(a => a.email !== email);
    });
  }, []);

  // Fetch calendar events from ALL linked accounts for a date range
  const fetchCalendarEvents = useCallback(async (startDate, endDate) => {
    // Refresh from sessionStorage in case state is stale
    const accounts = linkedAccounts.length > 0 ? linkedAccounts : loadLinkedAccounts();
    const validAccounts = accounts.filter(a => a.expiresAt > Date.now());

    if (validAccounts.length === 0) return null;

    const allEvents = [];
    const timeMin = new Date(startDate).toISOString();
    const timeMax = new Date(endDate + 'T23:59:59').toISOString();
    let anyExpired = false;

    for (const account of validAccounts) {
      for (const cal of account.calendars) {
        try {
          const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(cal.id)}/events?` +
            `timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&maxResults=100`;
          const res = await fetch(url, {
            headers: { Authorization: `Bearer ${account.token}` },
          });
          if (res.status === 401) {
            anyExpired = true;
            continue;
          }
          if (!res.ok) continue;
          const data = await res.json();
          for (const ev of (data.items || [])) {
            allEvents.push({
              title: ev.summary || '(No title)',
              date: ev.start?.date || ev.start?.dateTime?.split('T')[0] || '',
              startTime: ev.start?.dateTime ? ev.start.dateTime.split('T')[1]?.substring(0, 5) : null,
              endTime: ev.end?.dateTime ? ev.end.dateTime.split('T')[1]?.substring(0, 5) : null,
              allDay: !!ev.start?.date,
              calendar: cal.name,
              account: account.email.split('@')[0],
            });
          }
        } catch (err) {
          console.warn(`Failed to fetch calendar ${cal.name} (${account.email}):`, err);
        }
      }
    }

    // Remove expired accounts from state
    if (anyExpired) {
      setLinkedAccounts(prev => prev.filter(a => a.expiresAt > Date.now()));
    }

    return allEvents.length > 0 ? allEvents : (anyExpired ? null : []);
  }, [linkedAccounts]);

  // Initialize Google Identity Services
  const initializeGSI = useCallback(() => {
    if (gsiInitialized.current || !window.google?.accounts?.id) return;

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    if (googleBtnRef.current) {
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: 300,
      });
      gsiInitialized.current = true;
    }
  }, [handleCredentialResponse]);

  // Load Google Identity Services script + restore session
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (s && !isEmailAllowed(s.user?.email)) {
        supabase.auth.signOut();
        setSession(null);
      } else {
        setSession(s);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      if (s && !isEmailAllowed(s.user?.email)) {
        supabase.auth.signOut();
        setSession(null);
        setAuthError('Access denied. This app is restricted to authorized accounts only.');
      } else {
        setSession(s);
      }
      setLoading(false);
    });

    if (!document.getElementById('google-gsi-script')) {
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => setTimeout(() => initializeGSI(), 100);
      document.head.appendChild(script);
    } else if (window.google?.accounts?.id) {
      initializeGSI();
    }

    return () => subscription.unsubscribe();
  }, [initializeGSI]);

  // Re-render Google button when not signed in
  useEffect(() => {
    if (!session && !loading && googleBtnRef.current) {
      gsiInitialized.current = false;
      initializeGSI();
    }
  }, [session, loading, initializeGSI]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    // Revoke all calendar tokens
    for (const account of linkedAccounts) {
      if (window.google?.accounts?.oauth2) {
        try { window.google.accounts.oauth2.revoke(account.token); } catch {}
      }
    }
    setLinkedAccounts([]);
    sessionStorage.removeItem('gcal_accounts');
    gsiInitialized.current = false;
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  if (loading) {
    return (
      <div style={{display:"flex",justifyContent:"center",alignItems:"center",height:"100vh",background:"#faf9f6"}}>
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:"32px",marginBottom:"12px"}}>🍽️</div>
          <div style={{color:"#2d6a4f",fontWeight:"600",fontSize:"16px"}}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div style={{display:"flex",justifyContent:"center",alignItems:"center",height:"100vh",background:"#faf9f6",fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif"}}>
        <div style={{textAlign:"center",background:"white",padding:"48px 40px",borderRadius:"16px",boxShadow:"0 4px 24px rgba(0,0,0,0.08)",maxWidth:"400px",width:"90%"}}>
          <div style={{fontSize:"48px",marginBottom:"8px"}}>🍽️</div>
          <h1 style={{color:"#2d6a4f",fontSize:"24px",marginBottom:"4px"}}>Rogers Family</h1>
          <h2 style={{color:"#666",fontSize:"16px",fontWeight:"400",marginBottom:"32px"}}>Meal Planner</h2>
          <div style={{display:"flex",justifyContent:"center",marginBottom:"16px"}}>
            <div ref={googleBtnRef}></div>
          </div>
          {authError && (
            <p style={{marginTop:"16px",fontSize:"13px",color:"#dc2626",fontWeight:"500"}}>{authError}</p>
          )}
          <p style={{marginTop:"24px",fontSize:"12px",color:"#999",lineHeight:"1.5"}}>
            Sign in with your Google account to access the meal planner.
          </p>
        </div>
      </div>
    );
  }

  return (
    <AppErrorBoundary>
      <RogersFamilyMealPlanner
        session={session}
        onSignOut={signOut}
        linkedCalendarAccounts={linkedAccounts}
        onLinkCalendarAccount={linkCalendarAccount}
        onUnlinkCalendarAccount={unlinkCalendarAccount}
        fetchCalendarEvents={fetchCalendarEvents}
      />
    </AppErrorBoundary>
  );
}
